import React, { useState, useEffect } from "react";
import { useAuth, useLanguage, API } from "../App";
import AdminLayout from "../components/AdminLayout";
import { FeatureGate } from "../components/FeatureGate";
import WhatsAppSimulator from "../components/WhatsAppSimulator";
import WhatsAppConnect from "../components/WhatsAppConnect";
import WhatsAppUpsellDashboard from "../components/WhatsAppUpsellDashboard";
import AiCustomerCareSimulator from "../components/AiCustomerCareSimulator";
import WhatsAppActivationWizard from "../components/WhatsAppActivationWizard";
import { Button } from "../components/ui/button";
import {
  MessageSquare,
  TrendingUp,
  Users,
  CalendarCheck,
  AlertTriangle,
  RefreshCw,
  ArrowUpRight,
  Send,
  Loader2,
  ChevronDown,
  ChevronUp,
  Phone,
  Bot,
  BarChart3,
  Plug,
  Sparkles,
  Brain
} from "lucide-react";
import axios from "axios";

const WhatsAppDashboard = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [tab, setTab] = useState("dashboard");
  const [wizardOpen, setWizardOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selectedConv, setSelectedConv] = useState(null);

  useEffect(() => { if (tab === "dashboard") fetchData(); }, [filter, tab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, convsRes] = await Promise.all([
        axios.get(`${API}/wa/dashboard/stats`, { withCredentials: true }),
        axios.get(`${API}/wa/conversations`, {
          params: filter !== "all" ? { status: filter } : {},
          withCredentials: true
        })
      ]);
      setStats(statsRes.data);
      setConversations(convsRes.data.conversations || []);
    } catch (err) {
      console.error("WA dashboard error:", err);
    } finally {
      setLoading(false);
    }
  };

  const statusBadge = (status) => {
    const map = {
      active: { color: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400", label: "Attiva" },
      completed: { color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400", label: "Completata" },
      abandoned: { color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400", label: "Abbandonata" },
    };
    const s = map[status] || { color: "bg-slate-100 text-slate-600", label: status };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.color}`}>{s.label}</span>;
  };

  const sourceBadge = (source) => {
    const map = {
      inbound: { color: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400", label: "Inbound" },
      recovery: { color: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400", label: "Recovery" },
      reactivation: { color: "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400", label: "Riattivazione" },
      upsell: { color: "bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400", label: "Upsell" },
    };
    const s = map[source] || { color: "bg-slate-100 text-slate-600", label: source };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.color}`}>{s.label}</span>;
  };

  const formatTime = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <AdminLayout>
      <WhatsAppActivationWizard open={wizardOpen} onOpenChange={setWizardOpen} />
      <FeatureGate feature="whatsapp_basic" fallback={
        <div className="space-y-6" data-testid="whatsapp-dashboard-locked">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                <MessageSquare className="w-6 h-6 text-emerald-500" />
                WhatsApp Automation
              </h1>
              <p className="text-muted-foreground text-sm mt-1">
                {language === "it" ? "Attiva WhatsApp Business in 90 secondi" : "Activate WhatsApp Business in 90 seconds"}
              </p>
            </div>
          </div>
          <div className="max-w-lg mx-auto p-6 rounded-2xl border bg-card text-center space-y-4">
            <MessageSquare className="w-12 h-12 mx-auto text-emerald-500" />
            <h2 className="text-lg font-semibold">
              {language === "it" ? "Pronto ad attivare WhatsApp?" : "Ready to activate WhatsApp?"}
            </h2>
            <p className="text-sm text-muted-foreground">
              {language === "it"
                ? "Il team SmartBooker configura tutto per te. Reminder, conferme e assistenza AI in 90 secondi."
                : "The SmartBooker team sets everything up for you. Reminders, confirmations and AI support in 90s."}
            </p>
            <button
              onClick={() => setWizardOpen(true)}
              className="w-full p-4 rounded-2xl bg-gradient-to-br from-emerald-500 via-green-500 to-teal-600 text-white font-bold shadow-lg hover:shadow-xl hover:scale-[1.01] transition-all flex items-center justify-center gap-2"
              data-testid="wa-activate-btn"
            >
              <MessageSquare className="w-5 h-5" />
              {language === "it" ? "Attiva WhatsApp" : "Activate WhatsApp"}
            </button>
          </div>
        </div>
      }>
      <div className="space-y-6" data-testid="whatsapp-dashboard">
        {/* Header with tabs */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <MessageSquare className="w-6 h-6 text-emerald-500" />
              WhatsApp Automation
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              {language === "it" ? "Conversazioni, prenotazioni e simulatore bot" : "Conversations, bookings and bot simulator"}
            </p>
          </div>
          <div className="flex items-center gap-2 bg-muted rounded-full p-1">
            <button
              onClick={() => setTab("dashboard")}
              className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                tab === "dashboard" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid="wa-tab-dashboard"
            >
              <BarChart3 className="w-4 h-4" />
              Dashboard
            </button>
            <button
              onClick={() => setTab("connect")}
              className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                tab === "connect" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid="wa-tab-connect"
            >
              <Plug className="w-4 h-4" />
              {language === "it" ? "Connessione" : "Connection"}
            </button>
            <button
              onClick={() => setTab("upsell")}
              className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                tab === "upsell" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid="wa-tab-upsell"
            >
              <Sparkles className="w-4 h-4" />
              Upsell
            </button>
            <button
              onClick={() => setTab("ai")}
              className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                tab === "ai" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid="wa-tab-ai"
            >
              <Brain className="w-4 h-4" />
              AI
            </button>
            <button
              onClick={() => setTab("simulator")}
              className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                tab === "simulator" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid="wa-tab-simulator"
            >
              <Bot className="w-4 h-4" />
              Simulatore
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {tab === "simulator" ? (
          <WhatsAppSimulator />
        ) : tab === "ai" ? (
          <AiCustomerCareSimulator />
        ) : tab === "upsell" ? (
          <WhatsAppUpsellDashboard />
        ) : tab === "connect" ? (
          <div className="max-w-lg mx-auto space-y-6" data-testid="wa-connect-tab">
            <div className="text-center space-y-2">
              <h2 className="text-lg font-semibold text-foreground">
                {language === "it" ? "Collega il tuo WhatsApp Business" : "Connect your WhatsApp Business"}
              </h2>
              <p className="text-sm text-muted-foreground">
                {language === "it"
                  ? "Vuoi che SmartBooker configuri tutto per te? Apri la procedura guidata: 90 secondi, nessun token tecnico richiesto."
                  : "Want SmartBooker to set everything up? Open the guided wizard: 90 seconds, no technical token required."}
              </p>
            </div>
            <button
              onClick={() => setWizardOpen(true)}
              className="w-full p-5 rounded-2xl bg-gradient-to-br from-emerald-500 via-green-500 to-teal-600 text-white font-bold shadow-lg hover:shadow-xl hover:scale-[1.01] transition-all flex items-center justify-center gap-2"
              data-testid="wa-activate-btn"
            >
              <MessageSquare className="w-5 h-5" />
              {language === "it" ? "Attiva WhatsApp" : "Activate WhatsApp"}
            </button>
            <WhatsAppActivationWizard open={wizardOpen} onOpenChange={setWizardOpen} />
            <details className="rounded-xl border bg-card p-4 text-sm">
              <summary className="cursor-pointer font-medium text-muted-foreground hover:text-foreground">
                {language === "it" ? "Configurazione tecnica avanzata (Meta)" : "Advanced technical setup (Meta)"}
              </summary>
              <div className="mt-3">
                <WhatsAppConnect />
              </div>
            </details>
          </div>
        ) : (
          <>
            {/* Stats Cards */}
            {stats && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="wa-stats-cards">
                <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-5 text-white">
                  <MessageSquare className="w-7 h-7 opacity-80 mb-2" />
                  <p className="text-emerald-100 text-xs">{language === "it" ? "Conversazioni Mese" : "Month Conversations"}</p>
                  <p className="text-2xl font-bold mt-1">{stats.month_conversations}</p>
                </div>
                <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl p-5 text-white">
                  <CalendarCheck className="w-7 h-7 opacity-80 mb-2" />
                  <p className="text-blue-100 text-xs">{language === "it" ? "Prenotazioni Mese" : "Month Bookings"}</p>
                  <p className="text-2xl font-bold mt-1">{stats.month_bookings}</p>
                </div>
                <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl p-5 text-white">
                  <TrendingUp className="w-7 h-7 opacity-80 mb-2" />
                  <p className="text-amber-100 text-xs">{language === "it" ? "Tasso Conversione" : "Conversion Rate"}</p>
                  <p className="text-2xl font-bold mt-1">{stats.conversion_rate}%</p>
                </div>
                <div className="bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl p-5 text-white">
                  <RefreshCw className="w-7 h-7 opacity-80 mb-2" />
                  <p className="text-violet-100 text-xs">{language === "it" ? "Clienti Recuperati" : "Recovered Clients"}</p>
                  <p className="text-2xl font-bold mt-1">{stats.total_recovered}</p>
                </div>
              </div>
            )}

            {/* Source Breakdown */}
            {stats?.by_source && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  { key: "inbound", label: "Inbound", icon: MessageSquare, color: "text-violet-500" },
                  { key: "recovery", label: "Recovery", icon: AlertTriangle, color: "text-amber-500" },
                  { key: "reactivation", label: language === "it" ? "Riattivazione" : "Reactivation", icon: Users, color: "text-teal-500" },
                  { key: "upsell", label: "Upsell", icon: ArrowUpRight, color: "text-pink-500" },
                ].map(s => (
                  <div key={s.key} className="flex items-center gap-3 p-3 rounded-xl border border-border bg-card">
                    <s.icon className={`w-5 h-5 ${s.color}`} />
                    <div>
                      <p className="text-xs text-muted-foreground">{s.label}</p>
                      <p className="text-lg font-bold text-foreground">{stats.by_source[s.key] || 0}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Filters + Refresh */}
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                {[
                  { key: "all", label: language === "it" ? "Tutte" : "All" },
                  { key: "active", label: language === "it" ? "Attive" : "Active" },
                  { key: "completed", label: language === "it" ? "Completate" : "Completed" },
                  { key: "abandoned", label: language === "it" ? "Abbandonate" : "Abandoned" },
                ].map(f => (
                  <button
                    key={f.key}
                    onClick={() => setFilter(f.key)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      filter === f.key ? "bg-emerald-500 text-white" : "bg-muted text-muted-foreground hover:bg-muted/80"
                    }`}
                    data-testid={`wa-filter-${f.key}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={fetchData} className="rounded-full" data-testid="wa-refresh-btn">
                <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
                {language === "it" ? "Aggiorna" : "Refresh"}
              </Button>
            </div>

            {/* Conversations */}
            <div className="bg-card rounded-xl border border-border overflow-hidden">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
                </div>
              ) : conversations.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>{language === "it" ? "Nessuna conversazione WhatsApp" : "No WhatsApp conversations"}</p>
                  <p className="text-sm mt-1">{language === "it" ? "Prova il simulatore per testare i flussi!" : "Try the simulator to test flows!"}</p>
                  <Button variant="outline" size="sm" className="mt-3 rounded-full" onClick={() => setTab("simulator")} data-testid="wa-go-simulator">
                    <Bot className="w-4 h-4 mr-1.5" />
                    {language === "it" ? "Apri Simulatore" : "Open Simulator"}
                  </Button>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {conversations.map(conv => (
                    <div
                      key={conv.conversation_id}
                      className="p-4 hover:bg-muted/50 transition-colors cursor-pointer"
                      onClick={() => setSelectedConv(selectedConv?.conversation_id === conv.conversation_id ? null : conv)}
                      data-testid={`wa-conv-${conv.conversation_id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                            <Phone className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                          </div>
                          <div>
                            <p className="font-medium text-foreground text-sm">
                              {conv.context?.client_name || conv.customer_phone}
                            </p>
                            <p className="text-xs text-muted-foreground">{conv.customer_phone} — {conv.state}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {sourceBadge(conv.source)}
                          {statusBadge(conv.status)}
                          {conv.booking_id && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 flex items-center gap-1">
                              <CalendarCheck className="w-3 h-3" /> Prenotato
                            </span>
                          )}
                          <span className="text-xs text-muted-foreground hidden sm:inline">{formatTime(conv.last_message_at)}</span>
                          {selectedConv?.conversation_id === conv.conversation_id
                            ? <ChevronUp className="w-4 h-4 text-muted-foreground" />
                            : <ChevronDown className="w-4 h-4 text-muted-foreground" />
                          }
                        </div>
                      </div>

                      {selectedConv?.conversation_id === conv.conversation_id && conv.messages && (
                        <div className="mt-4 space-y-2 max-h-80 overflow-y-auto pl-12" data-testid="wa-conv-messages">
                          {conv.messages.map((msg, i) => (
                            <div key={i} className={`flex ${msg.role === "customer" ? "justify-start" : "justify-end"}`}>
                              <div className={`max-w-[80%] rounded-xl px-3 py-2 text-sm ${
                                msg.role === "customer"
                                  ? "bg-muted text-foreground"
                                  : "bg-emerald-500 text-white"
                              }`}>
                                <p className="whitespace-pre-wrap text-xs">{msg.text}</p>
                                <p className={`text-[10px] mt-1 ${msg.role === "customer" ? "text-muted-foreground" : "text-emerald-100"}`}>
                                  {formatTime(msg.timestamp)}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
      </FeatureGate>
    </AdminLayout>
  );
};

export default WhatsAppDashboard;
